import * as React from 'react';
import { Edit3, Info, Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableSearchInput,
  DashboardTableStatusTone,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { dateTimeFormat, numberFormat } from '../../../../lib/format';
import { dashboardT } from '../../../../lib/messages';
import { ShigoPermission } from '../../../auth/permissions';
import { useShigoPermissions } from '../../../auth/useShigoPermissions';
import { useDebugRulesQuery } from '../../hooks';
import {
  DebugSortDirection,
  DebugStatus,
  debugPageSizes,
  isDebugSortProperty,
  type DebugRule,
  type DebugSource,
} from '../../model';
import { useDebugRouteFilters } from '../../useDebugRouteFilters';
import { DebugRuleDialog } from '../DebugRuleDialog/DebugRuleDialog';

export function DebugTable() {
  const { can } = useShigoPermissions();
  const { filter, setPagination, setSearch, setSort } = useDebugRouteFilters();
  const query = useDebugRulesQuery(filter);
  const search = useDebouncedValue(filter.searchTerm, setSearch, 650);
  const [editingRule, setEditingRule] = React.useState<DebugRule | null | undefined>();
  const canCreate = can(ShigoPermission.DebugCreate);
  const canEdit = can(ShigoPermission.DebugEdit);

  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<DebugRule>([
        {
          id: 'status',
          kind: DashboardDataTableColumnKind.Status,
          header: dashboardT('debug.status'),
          headerClassName: 'w-32',
          className: 'w-32',
          label: (rule) => debugStatusLabel(rule.status),
          tone: (rule) => debugStatusTone(rule.status),
          sortable: true,
          sortKey: 'status',
        },
        {
          id: 'enabled',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('debug.enabled'),
          headerClassName: 'w-24',
          className: 'w-24',
          value: (rule) => dashboardT(rule.enabled ? 'debug.yes' : 'debug.no'),
          sortable: true,
          sortKey: 'enabled',
        },
        {
          id: 'enabledUntil',
          header: dashboardT('debug.enabledUntil'),
          headerClassName: 'w-36',
          className: 'w-36 whitespace-nowrap',
          sortable: true,
          sortKey: 'enabledUntil',
          cell: (rule) => <DebugDate value={rule.enabledUntil} />,
        },
        {
          id: 'eventTypes',
          header: dashboardT('debug.eventTypes'),
          headerClassName: 'w-48',
          className: 'w-48',
          cell: (rule) => {
            const value = rule.eventTypes.length
              ? rule.eventTypes.join(', ')
              : dashboardT('debug.all');
            return (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span className="block truncate">{value}</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-80">{value}</TooltipContent>
              </Tooltip>
            );
          },
        },
        {
          id: 'sampleRate',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('debug.sampleRate'),
          headerClassName: 'w-28',
          className: 'w-28',
          value: (rule) => formatSampleRate(rule.sampleRate),
          sortable: true,
          sortKey: 'sampleRate',
        },
        {
          id: 'sources',
          header: dashboardT('debug.sources'),
          headerClassName: 'w-24 text-center',
          className: 'w-24 text-center',
          cell: (rule) =>
            rule.sources.length ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground inline-flex cursor-help"
                    aria-label={dashboardT('debug.sourcesForRule')}
                  >
                    <Info className="h-4 w-4" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-96 whitespace-pre-line">
                  {formatSources(rule.sources)}
                </TooltipContent>
              </Tooltip>
            ) : (
              dashboardT('debug.notAvailable')
            ),
        },
        {
          id: 'enabledBy',
          header: dashboardT('debug.enabledBy'),
          headerClassName: 'w-56',
          className: 'w-56',
          sortable: true,
          sortKey: 'enabledBy',
          cell: (rule) => {
            const value = rule.enabledBy ?? dashboardT('debug.notAvailable');
            return (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span className="block truncate">{value}</span>
                </TooltipTrigger>
                <TooltipContent className="max-w-80">{value}</TooltipContent>
              </Tooltip>
            );
          },
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          hidden: !canEdit,
          cell: (rule) => (
            <DashboardTableRowActions label={dashboardT('debug.actionsFor', { id: rule.jobId })}>
              <DashboardTableRowActionItem
                onSelect={() => {
                  setEditingRule(rule);
                }}
              >
                <Edit3 className="h-4 w-4" aria-hidden="true" />
                {dashboardT('debug.edit')}
              </DashboardTableRowActionItem>
            </DashboardTableRowActions>
          ),
        },
      ]),
    [canEdit],
  );

  return (
    <main className="page-gutter min-w-0 py-6">
      <DashboardDataTable
        title={dashboardT('debug.title')}
        tableLabel={dashboardT('debug.title')}
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(rule) => rule.jobId}
        loading={query.isLoading}
        loadingMessage={dashboardT('debug.loading')}
        emptyMessage={dashboardT('debug.empty')}
        errorMessage={query.isError ? dashboardT('debug.loadError') : undefined}
        minWidthClassName="min-w-[1040px]"
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: debugPageSizes,
          disabled: query.isLoading,
          onChange: ({ page, pageSize }) => {
            setPagination(page, pageSize);
          },
        }}
        sort={{
          value: filter.sortProperty
            ? {
                key: filter.sortProperty,
                direction:
                  filter.sortDirection === DebugSortDirection.Asc
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (next) => {
            if (!next) {
              setSort(null, filter.sortDirection);
              return;
            }
            if (!isDebugSortProperty(next.key)) return;
            setSort(
              next.key,
              next.direction === DashboardDataTableSortDirection.Asc
                ? DebugSortDirection.Asc
                : DebugSortDirection.Desc,
            );
          },
        }}
        toolbar={
          <>
            <DashboardTableSearchInput
              value={search.draft}
              aria-label={dashboardT('debug.search')}
              placeholder={dashboardT('debug.search')}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            {canCreate ? (
              <Button
                onClick={() => {
                  setEditingRule(null);
                }}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {dashboardT('debug.create')}
              </Button>
            ) : null}
          </>
        }
      />
      {editingRule !== undefined ? (
        <DebugRuleDialog
          rule={editingRule}
          onClose={() => {
            setEditingRule(undefined);
          }}
        />
      ) : null}
    </main>
  );
}

function debugStatusTone(status: DebugStatus) {
  if (status === DebugStatus.Running || status === DebugStatus.Completed) {
    return DashboardTableStatusTone.Success;
  }
  if (status === DebugStatus.Failed) return DashboardTableStatusTone.Warning;
  return DashboardTableStatusTone.Neutral;
}

function debugStatusLabel(status: DebugStatus) {
  const labels: Record<DebugStatus, ReturnType<typeof dashboardT>> = {
    [DebugStatus.Running]: dashboardT('debug.status.running'),
    [DebugStatus.Completed]: dashboardT('debug.status.completed'),
    [DebugStatus.Canceled]: dashboardT('debug.status.canceled'),
    [DebugStatus.Failed]: dashboardT('debug.status.failed'),
    [DebugStatus.Unknown]: dashboardT('debug.status.unknown'),
  };
  return labels[status];
}

function DebugDate({ value }: { value: string | undefined }) {
  if (!value) return dashboardT('debug.notAvailable');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return dashboardT('debug.notAvailable');
  return (
    <span className="flex flex-col">
      <span>{dateTimeFormat().format(date)}</span>
      <span className="text-muted-foreground text-xs">
        {dateTimeFormat({ hour: '2-digit', hour12: false, minute: '2-digit' }).format(date)}
      </span>
    </span>
  );
}

function formatSampleRate(value: number | undefined) {
  if (value === undefined) return dashboardT('debug.notAvailable');
  return numberFormat({ style: 'percent', maximumFractionDigits: 2 }).format(value);
}

function formatSources(sources: readonly DebugSource[]) {
  return sources
    .map((source) => {
      const sourceType = source.sourceType.replaceAll('_', ' ');
      const secondaryId = source.secondaryId ? ` (${source.secondaryId})` : '';
      return `${sourceType}: ${source.name ?? dashboardT('debug.notAvailable')}${secondaryId}`;
    })
    .join('\n');
}
